import './SplitInput.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useMultipleRefs } from 'hooks/useMultipleRefs';
import { KEY_BACK_SPACE, KEY_LEFT, KEY_RIGHT } from 'constants/keycode';
import { useOutsideAlerter } from 'hooks/useOutsideAlerter';
import useAfterMountEffect from '../../hooks/useAfterMountEffect';
import IconSuccess from 'assets/icons/icon-success-circle.svg';

export const DEFAULT_CHARACTER_FILLER = ' ';
const PASSWORD_STYLE_CLASS = 'password-font-type';
export const SplitInput = ({
    howManyCharacters,
    onChange,
    value,
    onFocusIn,
    onFocusOut,
    hasError,
    noPaste,
    showCharacters,
    className,
    label,
    autoComplete,
    validateField,
    isCompleted,
    isDisabled,
    isSuccess = false,
    ariaLabel = 'input',
    type = 'text',
}) => {
    const { allReferences } = useMultipleRefs(howManyCharacters);
    const [allInputTimeOut, setAllInputTimeOut] = useState({});

    const hasBeenFocused = useRef(false);

    const componentRef = useRef(null);
    const MAX_LENGTH = 1;

    const makeInputValue = () => {
        return {
            text: allReferences.map(reference => reference.current.value || DEFAULT_CHARACTER_FILLER).join(''),
        };
    };

    const handleOnClickOut = () => {
        if (!hasBeenFocused.current) return;
        const { text } = makeInputValue();
        const isIncomplete = text.length > 0 && text.length < howManyCharacters;
        onFocusOut && onFocusOut(isIncomplete);
        hasBeenFocused.current = false;
    };

    const handleOnFocusIn = () => {
        hasBeenFocused.current = true;
        onFocusIn && onFocusIn();
    };

    const changeToPasswordType = reference => {
        reference.current.classList.add(PASSWORD_STYLE_CLASS);
    };

    const changeToOriginalType = reference => {
        reference.current.classList.remove(PASSWORD_STYLE_CLASS);
    };

    useEffect(() => {
        value?.split('').forEach((letter, index) => {
            allReferences[index].current.value = letter === DEFAULT_CHARACTER_FILLER ? '' : letter;
        });
    }, [value]);

    useEffect(() => {
        isCompleted && allReferences[howManyCharacters - 1]?.current?.blur();
    }, [isCompleted]);

    useAfterMountEffect(() => {
        allReferences.forEach((reference, index) => {
            clearTimeout(allInputTimeOut[index]);
            if (showCharacters && reference.current.value) {
                changeToOriginalType(reference);
            }

            if (!showCharacters && reference.current.value) {
                changeToPasswordType(reference);
            }
        });
    }, [showCharacters]);

    useOutsideAlerter(componentRef, handleOnClickOut);

    const fireOnChange = () => {
        const { text } = makeInputValue();
        onChange({ text });
    };

    const moveForward = index => {
        const nextInput = allReferences[index + 1];
        nextInput && nextInput.current && nextInput.current.focus();
    };

    const movePrev = index => {
        const prevInput = allReferences[index - 1];
        prevInput?.current?.focus();
    };

    const startHideCharacterTimeout = index => {
        if (allReferences[index - 1]) {
            changeToPasswordType(allReferences[index - 1]);
        }

        const inputTimeout = setTimeout(() => {
            if (allReferences[index]?.current?.value) {
                changeToPasswordType(allReferences[index]);
            }
        }, 2000);

        setAllInputTimeOut(prevState => ({ ...prevState, [index]: inputTimeout }));
    };
    const handleKeyDown = event => {
        if (!validateField(event.key) || event.repeat) {
            event.preventDefault();
        }
    };

    const handleKeyUp = (event, index) => {
        event.which === KEY_LEFT && movePrev(index);
        event.which === KEY_RIGHT && moveForward(index);

        if (event.which !== KEY_BACK_SPACE) return;

        if (event.target.value === '') {
            if (allReferences[index - 1]) {
                allReferences[index - 1].current.value = '';
            }
            movePrev(index);
        }

        allReferences[index].current.value = '';
        fireOnChange();
    };

    const handleChange = (event, index) => {
        const { value: text } = event.target;

        if (!validateField(text) && text !== '') {
            allReferences[index].current.value = '';

            return;
        }

        fireOnChange();
        moveForward(index);
        !showCharacters && startHideCharacterTimeout(index);
    };

    const handlePaste = event => {
        event.preventDefault();
        if (noPaste) return;

        const clipBoardText = event.clipboardData.getData('Text').split('');
        allReferences.forEach((reference, index) => {
            if (!clipBoardText[index]) return;

            const letterToPaste = clipBoardText[index];
            reference.current.value = validateField(letterToPaste) ? letterToPaste : DEFAULT_CHARACTER_FILLER;
        });

        fireOnChange();
        clipBoardText.length >= howManyCharacters
            ? event.target.blur()
            : allReferences[clipBoardText.length].current.focus();
    };

    return (
        <div className={`input-container ${className}-container ${hasError ? 'error' : ''}`}>
            {label && <label>{label}</label>}
            <div className="input-container-code">
                <div className={`code-container ${className} ${isDisabled ? 'bg-disabled' : ''}`} ref={componentRef}>
                    {allReferences.map((ref, index) => (
                        <input
                            aria-label={ariaLabel}
                            className={`char-code ${className}-char ${ref?.current?.value && 'filled'}`}
                            ref={ref}
                            key={index}
                            data-testid={`split-input-${index}`}
                            type={type}
                            maxLength={MAX_LENGTH}
                            onKeyDown={handleKeyDown}
                            onKeyUp={event => handleKeyUp(event, index)}
                            onPaste={handlePaste}
                            onFocus={handleOnFocusIn}
                            autoComplete={autoComplete}
                            onChange={event => handleChange(event, index)}
                            disabled={isDisabled}
                        />
                    ))}
                </div>
                {isSuccess && !hasError && (
                    <img alt="Success" className="input-container-code__icon-success" src={IconSuccess} />
                )}
            </div>
        </div>
    );
};
